import { type Scene } from 'three'
import { RigQualityReport, type RigQualitySummary, type RigQualitySeverity } from './RigQualityReport'
import { escape_html } from '../../lib/html.ts'

/**
 * Always-visible (when toggled on) floating panel showing the latest rig
 * quality summary. Polls `RigQualityReport.analyze_scene` on a slow interval
 * (the underlying analysis is cheap but still walks the scene). The user can
 * leave it on while working through the EditSkeleton / WeightSkin flow to
 * see issues clear out as they fix things.
 *
 * Independent of the existing one-shot RigQualityReport modal - this is a
 * persistent inspector, not a "click to open" report.
 */

export interface LiveRigQualityPanelOptions {
  scene: Scene
  refresh_ms?: number
}

const DEFAULT_REFRESH_MS = 1500

export class LiveRigQualityPanel {
  private active: boolean = false
  private panel: HTMLDivElement | undefined
  private timer: number | null = null

  constructor (private readonly options: LiveRigQualityPanelOptions) {}

  public is_active (): boolean { return this.active }

  public set_active (active: boolean): void {
    if (this.active === active) return
    this.active = active
    if (active) {
      this.ensure_panel()
      this.refresh()
      const ms = this.options.refresh_ms ?? DEFAULT_REFRESH_MS
      this.timer = window.setInterval(() => this.refresh(), ms)
    } else {
      if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null }
      if (this.panel !== undefined) this.panel.style.display = 'none'
    }
  }

  private refresh (): void {
    if (!this.active || this.panel === undefined) return
    const summary = RigQualityReport.analyze_scene(this.options.scene)
    this.panel.innerHTML = render_summary_html(summary)
  }

  private ensure_panel (): void {
    if (this.panel !== undefined) {
      this.panel.style.display = 'block'
      return
    }
    const el = document.createElement('div')
    el.id = 'live-rig-quality-panel'
    Object.assign(el.style, {
      position: 'fixed',
      top: '70px',
      left: '12px',
      maxWidth: '280px',
      background: 'rgba(20,20,20,0.92)',
      color: '#eee',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: '4px',
      padding: '0.5rem 0.75rem',
      font: '12px/1.4 system-ui, sans-serif',
      zIndex: '1000',
      pointerEvents: 'auto'
    })
    document.body.appendChild(el)
    this.panel = el
  }
}

function render_summary_html (summary: RigQualitySummary): string {
  const error_count = summary.issues.filter(i => i.severity === 'error').length
  const warning_count = summary.issues.filter(i => i.severity === 'warning').length
  const status =
    error_count > 0 ? { label: 'Needs attention', color: '#d4453a' }
    : warning_count > 0 ? { label: 'Usable with warnings', color: '#e69500' }
    : { label: 'Ready', color: '#4cd964' }

  const issue_rows = summary.issues.length === 0
    ? '<div style="opacity: 0.7;">No issues detected.</div>'
    : summary.issues.map(i => `<div style="display:flex; gap:0.4rem; margin-top:2px;"><span>${severity_icon(i.severity)}</span><span>${escape_html(i.message)}</span></div>`).join('')
  const weight_text = summary.unweighted_vertex_count > 0 ? ` | ${summary.unweighted_vertex_count} unweighted` : ''

  return `
    <div style="display:flex; align-items:center; gap:0.5rem; margin-bottom: 4px;">
      <span style="background:${status.color}; color:#000; border-radius:3px; padding:1px 6px; font-weight:700;">${escape_html(status.label)}</span>
      <span style="font-weight:700;">${summary.score}/100</span>
    </div>
    <div style="font-size: 0.85em; opacity: 0.85; margin-bottom: 4px;">
      ${escape_html(summary.detected_convention)} | ${summary.bone_count} bones | ${summary.skinned_mesh_count} meshes | ${summary.largest_dimension.toFixed(2)} units${weight_text}
    </div>
    <div>${issue_rows}</div>
  `
}

function severity_icon (s: RigQualitySeverity): string {
  if (s === 'error') return '<span style="color:#d4453a;">x</span>'
  if (s === 'warning') return '<span style="color:#e69500;">!</span>'
  return '<span style="color:#888;">i</span>'
}

